import {
  DEFAULT_GAME_SETTINGS,
  GAME_SETTINGS_FIELD_META,
  GAME_SETTINGS_GROUP_LABEL,
  getGameSettings,
  normalizeGameSettings,
  resetGameSettings,
  saveGameSettings,
  type GameSettings,
  type GameSettingsFieldMeta,
} from "@/game/model/game-settings";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function renderField(settings: GameSettings, meta: GameSettingsFieldMeta): string {
  const value = settings[meta.key];
  const step = meta.step ?? 1;
  const min = meta.min ?? "";
  const max = meta.max ?? "";
  return `
    <label class="field gm-settings-field">
      <span class="field-label">${escapeHtml(meta.label)}</span>
      <input
        type="number"
        class="input gm-settings-input"
        data-key="${meta.key}"
        value="${value}"
        step="${step}"
        ${min !== "" ? `min="${min}"` : ""}
        ${max !== "" ? `max="${max}"` : ""}
      />
      <span class="hint gm-settings-effect">ถ้าเพิ่ม: ${escapeHtml(meta.effectIfHigher)}</span>
      <span class="hint gm-settings-default">ค่าเริ่มต้น: ${DEFAULT_GAME_SETTINGS[meta.key]}</span>
    </label>
  `;
}

function renderGroup(
  group: GameSettingsFieldMeta["group"],
  settings: GameSettings,
): string {
  const fields = GAME_SETTINGS_FIELD_META.filter((f) => f.group === group);
  return `
    <section class="gm-settings-group" aria-labelledby="gm-settings-${group}">
      <h3 class="gm-settings-group-title" id="gm-settings-${group}">${GAME_SETTINGS_GROUP_LABEL[group]}</h3>
      <div class="gm-settings-grid">${fields.map((f) => renderField(settings, f)).join("")}</div>
    </section>
  `;
}

export function renderGmSettingsSectionHtml(): string {
  const settings = getGameSettings();
  return `
    <p class="hint gm-settings-lead">
      ค่าพื้นฐานของเกม (บันทึกในเครื่องนี้ — localStorage) ใช้กับสูตรสเตต ตีธรรมดา และมอนสเตอร์ชิ้นใหม่
      แต่ละช่องมีคำอธิบาย <strong>ถ้าเพิ่ม</strong> — ค่ายิ่งสูง ผลตามบรรทัดนั้นยิ่งแรง (ลดค่ากลับด้านกัน)
    </p>
    <form class="gm-settings-form" id="gm-settings-form">
      ${renderGroup("player", settings)}
      ${renderGroup("monster", settings)}
      ${renderGroup("combat", settings)}
      ${renderGroup("homeStructure", settings)}
      <div class="gm-settings-actions">
        <button type="submit" class="btn btn-primary">บันทึกค่าพื้นฐาน</button>
        <button type="button" class="btn btn-ghost" id="gm-settings-reset">รีเซ็ตเป็นค่าเริ่มต้น</button>
      </div>
      <p class="hint gm-settings-foot" id="gm-settings-status" role="status"></p>
    </form>
  `;
}

export function bindGmSettingsSection(root: HTMLElement): () => void {
  const form = root.querySelector<HTMLFormElement>("#gm-settings-form");
  const status = root.querySelector<HTMLElement>("#gm-settings-status");
  const setStatus = (msg: string) => {
    if (status) status.textContent = msg;
  };

  const onSubmit = (event: Event) => {
    event.preventDefault();
    if (!form) return;
    const patch: Partial<GameSettings> = {};
    for (const input of form.querySelectorAll<HTMLInputElement>(".gm-settings-input")) {
      const key = input.dataset.key as keyof GameSettings | undefined;
      if (!key) continue;
      const n = Number(input.value);
      if (!Number.isFinite(n)) continue;
      (patch as Record<string, number>)[key] = n;
    }
    saveGameSettings(normalizeGameSettings({ ...getGameSettings(), ...patch }));
    setStatus("บันทึกแล้ว — ตีธรรมดา/สเตตมีผลทันที (รีเฟรชเกมถ้าแอนิไม่เปลี่ยน)");
  };

  const onReset = () => {
    resetGameSettings();
    if (form) {
      const s = getGameSettings();
      for (const input of form.querySelectorAll<HTMLInputElement>(".gm-settings-input")) {
        const key = input.dataset.key as keyof GameSettings | undefined;
        if (!key) continue;
        input.value = String(s[key]);
      }
    }
    setStatus("รีเซ็ตเป็นค่าเริ่มต้นแล้ว");
  };

  const onResetClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id !== "gm-settings-reset" && !target.closest("#gm-settings-reset")) return;
    onReset();
  };

  form?.addEventListener("submit", onSubmit);
  root.addEventListener("click", onResetClick);

  return () => {
    form?.removeEventListener("submit", onSubmit);
    root.removeEventListener("click", onResetClick);
  };
}
